import { Helmet } from "react-helmet-async";

export default function ShellPilotPrivacy() {
  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#26313B] p-8 md:p-16 transition-colors duration-300">
      <Helmet>
        <title>Privacy Policy – ShellPilot</title>
        <meta
          name="description"
          content="Privacy Policy for ShellPilot, the SSH terminal and SFTP client by Cosmovex."
        />
        <link
          rel="canonical"
          href="https://cosmovexapps.com/apps/shellpilot/privacy-policy"
        />
      </Helmet>

      <h1 className="text-4xl font-bold mb-6 text-[#A8720E]">
        Privacy Policy – ShellPilot
      </h1>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        <strong>ShellPilot</strong> is an SSH terminal and SFTP client created by{" "}
        <strong>Cosmovex</strong>. This Privacy Policy explains what information
        ShellPilot may process, how it is used, where it is stored, and what
        controls you have when using the app.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        1. Information We Process
      </h2>

      <ul className="list-disc list-inside text-[#3D4B59] leading-relaxed space-y-2 max-w-4xl">
        <li>
          <strong>Saved connection data:</strong> connection names, hostnames or
          IP addresses, ports, usernames, connection groups, startup commands,
          favorite state, and similar connection metadata you choose to save in
          the app.
        </li>
        <li>
          <strong>Authentication materials:</strong> passwords, imported private
          keys, generated private keys, and key passphrase-related secrets. These
          are intended to be stored using secure device storage such as Android
          Keystore or iOS Keychain where supported.
        </li>
        <li>
          <strong>Known host information:</strong> SSH host key fingerprints and
          host verification history used to help protect you against server
          impersonation.
        </li>
        <li>
          <strong>Snippets and preferences:</strong> saved command snippets,
          snippet variables, terminal theme and font selections, biometric lock
          preference, SFTP favorites, recent paths, and other app settings.
        </li>
        <li>
          <strong>Account and purchase data:</strong> if you sign in, we may
          process your Firebase Auth account identifier, email address, sign-in
          provider, and purchase entitlement status for ShellPilot Pro.
        </li>
        <li>
          <strong>Diagnostics and usage data:</strong> crash diagnostics, basic
          feature usage events, app version, device type, and operating system
          information collected via Firebase Analytics and Firebase Crashlytics.
        </li>
        <li>
          <strong>Advertising identifiers:</strong> if you use the free tier, ad
          delivery and measurement may involve identifiers and ad interaction data
          processed by Google AdMob.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        2. What ShellPilot Does Not Intend to Collect
      </h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        ShellPilot is designed as a client application for connecting to your own
        servers. We do not intend to collect, read, or upload the content of
        your SSH sessions, terminal output, remote files, or command history to
        our own servers as part of normal app functionality. Remote commands and
        file content are transmitted between your device and the servers you
        connect to.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        3. How We Use Information
      </h2>

      <ul className="list-disc list-inside text-[#3D4B59] leading-relaxed space-y-2 max-w-4xl">
        <li>To store and manage your SSH connections, keys, snippets, and settings.</li>
        <li>To authenticate you and restore Pro access across reinstalls or devices.</li>
        <li>To verify one-time purchases and maintain purchase entitlement status.</li>
        <li>To improve app stability, performance, onboarding, and feature quality.</li>
        <li>To protect access to the app through biometric lock and host verification features.</li>
        <li>To provide ads in the free tier and remove them for Pro users.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        4. Local Storage and Secure Storage
      </h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        Many ShellPilot data points are stored locally on your device, including
        saved connections, snippets, settings, known hosts, and SFTP navigation
        state. Sensitive data such as passwords and private keys are intended to
        be stored using secure device storage where available. You are
        responsible for protecting access to your device and for using device
        security features such as passcodes, biometrics, and full-disk
        encryption.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        5. Account and Purchase Verification
      </h2>

      <p className="text-[#3D4B59] leading-relaxed mb-4 max-w-4xl">
        ShellPilot Pro may use account sign-in and backend purchase verification
        to restore purchases and validate entitlements. This may include:
      </p>

      <ul className="list-disc list-inside text-[#3D4B59] leading-relaxed space-y-2 max-w-4xl">
        <li>Firebase Authentication account identifiers and email address.</li>
        <li>Store product identifiers, purchase tokens, transaction or order IDs, and entitlement timestamps.</li>
        <li>Server-side entitlement records used to confirm whether Pro access is active.</li>
      </ul>

      <p className="text-[#3D4B59] leading-relaxed mt-4 mb-6 max-w-4xl">
        We use this information to reduce fraud, restore Pro on future installs,
        and support purchase verification. We do not use purchase data to profile
        you for unrelated advertising purposes.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">6. Analytics and Crash Reporting</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        ShellPilot uses Firebase Analytics and Firebase Crashlytics to understand
        app reliability and high-level usage patterns. This may include screen
        views, feature usage events, crash traces, device model, operating system
        version, and app version. We do not intentionally send SSH passwords,
        private keys, server contents, or terminal output as analytics data.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">7. Advertising</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        Free users may see ads served through Google AdMob. AdMob may process
        device identifiers, approximate location, and interaction data in
        accordance with Google&apos;s policies. Pro users are intended to have an
        ad-free experience.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">8. Data Sharing</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        We may rely on service providers such as Google Firebase, Google Play,
        Apple, and Google AdMob to provide authentication, analytics, crash
        reporting, purchase verification, and advertising services. We do not
        sell your personal data. Information may also be disclosed when required
        by law, to protect our legal rights, or to respond to security abuse.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">9. Data Retention</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        Local device data remains on your device until you delete it, uninstall
        the app, or use in-app reset and deletion controls. Account and
        entitlement records may be retained for security, fraud prevention,
        legal, accounting, and purchase restoration purposes for as long as
        reasonably necessary.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        10. Account Deletion and Data Deletion
      </h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        If you created a ShellPilot account and want your account-related data
        deleted, please see the account deletion page at{" "}
        <a
          href="https://cosmovexapps.com/apps/shellpilot/account-deletion"
          className="text-[#0F6BBF] underline hover:text-[#0C5AA3]"
        >
          https://cosmovexapps.com/apps/shellpilot/account-deletion
        </a>
        . Deleting your account does not automatically delete data stored only on
        your local device unless you also remove it from the app or uninstall the
        app.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">11. Children&apos;s Privacy</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        ShellPilot is not directed to children under 13, and we do not knowingly
        collect personal data from children.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">12. Changes to This Policy</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        We may update this Privacy Policy from time to time. Material changes
        will be reflected by updating this page and the revision date below.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">13. Contact</h2>

      <p className="text-[#3D4B59] leading-relaxed max-w-4xl">
        For privacy, account, or data questions related to ShellPilot, contact{" "}
        <a
          href="mailto:trionapps@gmail.com"
          className="text-[#0F6BBF] underline hover:text-[#0C5AA3]"
        >
          trionapps@gmail.com
        </a>
        .
      </p>

      <p className="text-[#8A93A2] mt-10">
        Last updated: March 11, 2026
      </p>
    </div>
  );
}
